import {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useAsync, useAsyncFn} from 'react-use';
import {
  createSlamPointCloudsUseCase,
  deleteSlamPointCloudsUseCase,
  downloadSlamPointCloud,
  getAllInventoryPlotsByForestMassUuidUseCase,
  getAllLidarTechnologiesUseCase,
  getAllSpeciesUseCase,
  getCurrentUserUseCase,
  getForestMassByForestMassSpeciesUuid,
  getForestMassSpeciesByForestMassUuidUseCase,
  getInventoryPlotByUuidUseCase,
  getInventoryStemsByInventoryPlotUuidUseCase,
  getSlamPointCloudsByInventoryPlotUuidUseCase,
  opWeightIntentoryPlotsUseCase,
  publishErrorUseCase,
  reUploadSlamPointCloud,
} from '@/compositionRoot';
import type {BackState} from '@/domain/commonTypes';
import type {InventoryPlot} from '@/domain/entities/InventoryPlot';
import type {WeightInventoryPlot} from '@/domain/entities/Op';
import type {SlamPointCloudCreate} from '@/domain/entities/SlamPointCloud';
import UserPermissions from '@/domain/useCases/userPermissions';

export const useController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {uuid} = useParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [inventoryPlot, setInventoryPlot] = useState<InventoryPlot | undefined>(undefined);
  const [user, fetchUser] = useAsyncFn(() => getCurrentUserUseCase.execute(), []);

  const handlePermissions = useMemo(() => {
    return new UserPermissions(user.value);
  }, [user.value]);

  //Get Inventory Plot Detail
  const {loading: loadingInventoryPlot} = useAsync(
    () => getInventoryPlotByUuidUseCase.execute(uuid || '').then(setInventoryPlot),
    [uuid],
  );

  //Get Forest Mass
  const {value: forestMass, loading: forestMassLoading} = useAsync(() => {
    if (inventoryPlot) return getForestMassByForestMassSpeciesUuid.execute(inventoryPlot.forestMassSpeciesUuid);
    return Promise.resolve(undefined);
  }, [inventoryPlot]);

  //List Inventory Plot
  const {value: inventoryPlots, loading: inventoryPlotsLoading} = useAsync(() => {
    if (forestMass) return getAllInventoryPlotsByForestMassUuidUseCase.execute(forestMass.uuid);
    return Promise.resolve([]);
  }, [forestMass]);

  //List Forest Mass Species
  const {value: forestMassSpecies, loading: forestMassSpeciesLoading} = useAsync(() => {
    if (forestMass) return getForestMassSpeciesByForestMassUuidUseCase.execute(forestMass.uuid);
    return Promise.resolve([]);
  }, [forestMass]);

  // List Species
  const {value: species, loading: speciesLoading} = useAsync(() => getAllSpeciesUseCase.execute(), [uuid]);

  //List Inventory Stems
  const {value: inventoryStems, loading: inventoryStemsLoading} = useAsync(() => {
    if (inventoryPlot) return getInventoryStemsByInventoryPlotUuidUseCase.execute(inventoryPlot.uuid);
    return Promise.resolve(undefined);
  }, [inventoryPlot]);

  //List Lidar Technologies
  const {value: lidarTechnologies, loading: lidarTechnologiesLoading} = useAsync(() => {
    return getAllLidarTechnologiesUseCase.execute();
  }, []);

  //get slam point cloud if exists
  const [{value: slamPointCloud, loading: slamPointCloudLoading}, fetchSlamPointClouds] = useAsyncFn(() => {
    if (uuid) return getSlamPointCloudsByInventoryPlotUuidUseCase.execute(uuid);
    return Promise.resolve(undefined);
  }, [uuid]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: not necessary
  useEffect(() => {
    fetchSlamPointClouds();
    fetchUser();
  }, [uuid]);

  useEffect(() => {
    if (
      loadingInventoryPlot ||
      forestMassLoading ||
      inventoryPlotsLoading ||
      forestMassSpeciesLoading ||
      speciesLoading ||
      inventoryStemsLoading ||
      lidarTechnologiesLoading ||
      slamPointCloudLoading
    ) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [
    loadingInventoryPlot,
    forestMassLoading,
    inventoryPlotsLoading,
    forestMassSpeciesLoading,
    speciesLoading,
    inventoryStemsLoading,
    lidarTechnologiesLoading,
    slamPointCloudLoading,
  ]);

  const handleBack = () => {
    const state = location.state as BackState | null;
    navigate(state?.from ?? `../../forest-masses/${forestMass?.uuid}`, {
      replace: true,
      state: state?.backState,
    });
  };

  const handleInventoryPlotSelect = (uuid: string) => navigate(`../../inventory-plots/${uuid}`);

  const handleDeleteSlamPointCloud = async (pointCloudUuid: string) => {
    await deleteSlamPointCloudsUseCase.execute(pointCloudUuid);
    await fetchSlamPointClouds();
  };

  const handleDownloadSlamPointCloud = async (pointCloudUuid: string) => {
    await downloadSlamPointCloud.execute(pointCloudUuid);
  };

  const handleReuploadSlamPointCloud = async (pointCloudUuid: string, file: File) => {
    try {
      setLoading(true);
      await reUploadSlamPointCloud.execute(pointCloudUuid, file);
    } catch (error) {
      publishErrorUseCase.execute(error as Error);
    } finally {
      setLoading(false);
    }
    await fetchSlamPointClouds();
  };

  const handleUploadSlamPointCloud = async (domain: Partial<SlamPointCloudCreate>, file: File) => {
    const entity = {
      ...domain,
      inventoryPlotUuid: inventoryPlot?.uuid,
    } as SlamPointCloudCreate;

    try {
      setLoading(true);
      await createSlamPointCloudsUseCase.execute(entity, file);
    } catch (error) {
      publishErrorUseCase.execute(error as Error);
    } finally {
      setLoading(false);
    }
    await fetchSlamPointClouds();
  };

  const handleUploadInventoryPlot = async (appraiserWeight: number) => {
    if (uuid) {
      const domain: WeightInventoryPlot = {
        inventoryPlotUuid: uuid,
        appraiserWeight: appraiserWeight,
      };
      const inventoryPlot = await opWeightIntentoryPlotsUseCase.execute(domain);
      setInventoryPlot(inventoryPlot);
    }
  };

  return {
    inventoryPlot,
    forestMass,
    inventoryPlots,
    forestMassSpecies,
    lidarTechnologies: lidarTechnologies || [],
    slamPointCloud,
    species,
    inventoryStems,
    loading,
    onBack: handleBack,
    userPermissions: handlePermissions,
    onInventoryPlotSelect: handleInventoryPlotSelect,
    onDeleteSlamPointCloud: handleDeleteSlamPointCloud,
    onUploadSlamPointCloud: handleUploadSlamPointCloud,
    onDownloadSlamPointCloud: handleDownloadSlamPointCloud,
    onReuploadSlamPointCloud: handleReuploadSlamPointCloud,
    onAppraiserWeightChange: handleUploadInventoryPlot,
  };
};

export default useController;
